'use client'

import { useCallback, useEffect, useState } from 'react'
import NextImage from 'next/image'
import { ActionIcon, Box, Group, Image, Loader, Modal, ModalCloseButton, Text } from '@mantine/core'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { redactMediaProps, redactPropsWithClassName } from '@phoenix-web/utils/general'
import { UploadedDealFiles } from '@phoenix-web/ux-gql/queries'
import { ACCEPTED_PHOTO_TYPES } from './PhotoUploader'
import { useImagePreloader } from './useImagePreloader'
import type { AcceptedPhotoTypes } from './PhotoUploader'

type PhotoCarouselProps = {
  photos: UploadedDealFiles
  initialIndex: number
  opened: boolean
  onClose: () => void
}

// Custom image component with loading state for modal
function ModalImageWithLoader({
  src,
  alt,
  width,
  height,
  style,
  onClick,
  isZoomed,
}: {
  src: string
  alt: string
  width: number
  height: number
  style?: React.CSSProperties
  onClick?: () => void
  isZoomed: boolean
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  // Reset loading state when src changes (when navigating between images)
  useEffect(() => {
    setIsLoading(true)
    setHasError(false)
  }, [src])

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        height: isZoomed ? 'auto' : '100%',
        display: 'flex',
        alignItems: isZoomed ? 'flex-start' : 'center',
        justifyContent: isZoomed ? 'flex-start' : 'center',
        minHeight: isZoomed ? 'auto' : '100%',
      }}
    >
      {isLoading && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
            backgroundColor: 'var(--mantine-color-light-gray-7)',
          }}
        >
          <Loader size="md" color="white" />
        </Box>
      )}

      {hasError && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
            backgroundColor: 'var(--mantine-color-light-gray-7)',
          }}
        >
          <Text c="white" size="md" fw={700}>
            Failed to load image
          </Text>
        </Box>
      )}

      <Image
        {...redactMediaProps}
        component={NextImage}
        width={width}
        height={height}
        src={src}
        alt={alt}
        priority
        sizes="100vw"
        fit="contain"
        style={{
          ...style,
          opacity: isLoading || hasError ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          zIndex: 1,
          alignSelf: isZoomed ? 'flex-start' : 'center',
        }}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false)
          setHasError(true)
        }}
        onClick={onClick}
      />
    </Box>
  )
}

export default function PhotoCarousel({ photos, initialIndex, opened, onClose }: PhotoCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isZoomed, setIsZoomed] = useState(false)

  useImagePreloader(photos, currentIndex, opened)

  // Reset index when initialIndex changes and modal is opened
  useEffect(() => {
    if (opened) {
      setCurrentIndex(initialIndex)
      setIsZoomed(false) // Reset zoom when opening modal
    }
  }, [initialIndex, opened])

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1))
  }, [photos.length])

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1))
  }, [photos.length])

  const handleImageClick = useCallback(() => {
    // Only allow zoom for images, not PDFs
    const currentFile = photos[currentIndex]
    const isImage = ACCEPTED_PHOTO_TYPES.includes(currentFile.content_type as AcceptedPhotoTypes)

    if (isImage) {
      setIsZoomed((prev) => !prev)
    }
  }, [currentIndex, photos])

  // Keyboard navigation
  useEffect(() => {
    if (!opened) return

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          handlePrevious()
          break
        case 'ArrowRight':
          handleNext()
          break
        case 'Escape':
          if (isZoomed) {
            setIsZoomed(false)
          } else {
            onClose()
          }
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [opened, handlePrevious, handleNext, onClose, isZoomed])

  const currentFile = photos[currentIndex]
  const isImage = ACCEPTED_PHOTO_TYPES.includes(currentFile.content_type as AcceptedPhotoTypes)
  const isZoomedImage = isZoomed && isImage
  const isPdf = !isImage

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="100%"
      centered
      padding={0}
      withCloseButton={false}
      styles={{
        body: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: isZoomedImage ? 'auto' : 'hidden',
        },
        content: {
          height: '100%',
          maxHeight: '100vh',
          overflow: 'hidden',
        },
      }}
    >
      <ModalCloseButton
        pos="fixed"
        top={16}
        right={16}
        style={{ zIndex: 4, backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        c="white"
        size="lg"
      />
      <Box
        pos="relative"
        style={{
          flex: isZoomedImage ? '1 0 auto' : 1,
          display: 'flex',
          alignItems: isZoomedImage ? 'flex-start' : 'center',
          justifyContent: isZoomedImage ? 'flex-start' : 'center',
          minHeight: isZoomedImage ? 'auto' : 0,
          padding: 0,
          height: isZoomedImage ? 'auto' : '100%',
        }}
      >
        {isImage ? (
          <ModalImageWithLoader
            src={currentFile.presigned_url}
            alt={currentFile.file_name}
            width={2000}
            height={2000}
            style={{
              maxWidth: '100%',
              maxHeight: isZoomed ? 'none' : '100%',
              width: isZoomed ? '100vw' : '100%',
              height: isZoomed ? 'auto' : '100%',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
            onClick={handleImageClick}
            isZoomed={isZoomed}
          />
        ) : isPdf ? (
          <iframe
            {...redactMediaProps}
            src={`${currentFile.presigned_url}#toolbar=0&navpanes=0&scrollbar=0`} // Final appended params are to remove the toolbar and scrollbar from the PDF viewer. Without this, the PDF viewer looks as though you selected the browser's "Print" option to view the PDF.
            width="100%"
            height="100%"
            style={{
              border: 'none',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
            title={currentFile.file_name}
          />
        ) : (
          <Text c="white">Unsupported file type</Text>
        )}

        <Group
          justify="space-between"
          pos="fixed"
          top="50%"
          left={0}
          right={0}
          px="md"
          style={{ transform: 'translateY(-50%)', zIndex: 3, pointerEvents: 'none' }}
        >
          <ActionIcon
            variant="filled"
            color="dark"
            size="xl"
            radius="xl"
            onClick={handlePrevious}
            aria-label="Previous file"
            style={{ pointerEvents: 'auto' }}
          >
            <FontAwesomeIcon icon={faChevronLeft} size="xl" />
          </ActionIcon>

          <ActionIcon
            variant="filled"
            color="dark"
            size="xl"
            radius="xl"
            onClick={handleNext}
            aria-label="Next file"
            style={{ pointerEvents: 'auto' }}
          >
            <FontAwesomeIcon icon={faChevronRight} size="xl" />
          </ActionIcon>
        </Group>
      </Box>
      <Group
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        p="md"
        justify="space-between"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 3 }}
      >
        <Text c="white" {...redactPropsWithClassName}>
          {currentFile.file_name}
        </Text>
        <Group gap="xs">
          {isZoomedImage && (
            <Text c="white" size="sm" style={{ fontStyle: 'italic' }}>
              Click to zoom out
            </Text>
          )}
          <Text c="white">
            {currentIndex + 1} of {photos.length}{' '}
            {/* Will say "Photo(s)" or "File(s)" depending on if the file is an image or not */}
            {photos.length === 1 ? (isImage ? 'Photo' : 'File') : isImage ? 'Photos' : 'Files'}
          </Text>
        </Group>
      </Group>
    </Modal>
  )
}
